import { buildPermission } from "portal/utils/auth";
import { ComponentType, FC, ReactNode } from "react";
import { FeatureFlag, useFeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { withFeatureFlag } from "portal/components/RequireFeatureFlag";

export const plantProfilesCustomerReadStatement = {
  or: [
    buildPermission(
      PermissionAction.read,
      PermissionResource.plant_category_profiles,
      PermissionDomain.customer
    ),
    buildPermission(
      PermissionAction.read,
      PermissionResource.plant_category_profiles,
      PermissionDomain.all
    ),
  ],
};

export const plantProfilesCustomerUpdateStatement = {
  or: [
    buildPermission(
      PermissionAction.update,
      PermissionResource.plant_category_profiles,
      PermissionDomain.customer
    ),
    buildPermission(
      PermissionAction.update,
      PermissionResource.plant_category_profiles,
      PermissionDomain.all
    ),
  ],
};

export const plantProfilesTemplatesReadStatement = buildPermission(
  PermissionAction.read,
  PermissionResource.plant_category_profiles,
  PermissionDomain.templates
);

export const plantProfilesTemplatesUpdateStatement = buildPermission(
  PermissionAction.update,
  PermissionResource.plant_category_profiles,
  PermissionDomain.templates
);

export const plantProfilesAnyReadStatement = {
  or: [
    ...plantProfilesCustomerReadStatement.or,
    plantProfilesTemplatesReadStatement,
  ],
};

export const almanacsCustomerReadStatement = {
  or: [
    buildPermission(
      PermissionAction.read,
      PermissionResource.almanacs,
      PermissionDomain.customer
    ),
    buildPermission(
      PermissionAction.read,
      PermissionResource.almanacs,
      PermissionDomain.all
    ),
  ],
};

export const almanacsTemplatesReadStatement = buildPermission(
  PermissionAction.read,
  PermissionResource.almanacs,
  PermissionDomain.templates
);

export const plantProfilesPageReadStatement = {
  and: [plantProfilesCustomerReadStatement, almanacsCustomerReadStatement],
};

export const plantProfilesAdminReadStatement = {
  and: [plantProfilesTemplatesReadStatement, almanacsTemplatesReadStatement],
};

export const plantProfilesListReadStatement = {
  or: [plantProfilesAdminReadStatement, plantProfilesPageReadStatement],
};

export const plantProfilesReadStatementForScope = (
  isAdmin: boolean
):
  | typeof plantProfilesAdminReadStatement
  | typeof plantProfilesPageReadStatement =>
  isAdmin ? plantProfilesAdminReadStatement : plantProfilesPageReadStatement;

export const plantProfilesUpdateStatementForScope = (
  isAdmin: boolean
):
  | typeof plantProfilesTemplatesUpdateStatement
  | typeof plantProfilesCustomerUpdateStatement =>
  isAdmin
    ? plantProfilesTemplatesUpdateStatement
    : plantProfilesCustomerUpdateStatement;

export const portalGlobalsReadStatement = buildPermission(
  PermissionAction.read,
  PermissionResource.portal_globals,
  PermissionDomain.all
);

export const portalGlobalsUpdateStatement = buildPermission(
  PermissionAction.update,
  PermissionResource.portal_globals,
  PermissionDomain.all
);

export const useCanReadPlantProfile = (isAdmin: boolean): boolean =>
  useAuthorizationRequired(plantProfilesReadStatementForScope(isAdmin));

export const useCanUpdatePlantProfile = (isAdmin: boolean): boolean =>
  useAuthorizationRequired(plantProfilesUpdateStatementForScope(isAdmin));

export const useCanSeePlantProfilesPage = (): boolean => {
  const { isEnabled: hasCategoryCollection } = useFeatureFlag(
    FeatureFlag.CATEGORY_COLLECTION
  );
  const isAuthorized = useAuthorizationRequired(plantProfilesPageReadStatement);
  return Boolean(isAuthorized && hasCategoryCollection);
};

export const useCanSeeAdminPlantProfiles = (): boolean =>
  useAuthorizationRequired(plantProfilesAdminReadStatement);

export const useCanSeePortalGlobals = (): boolean =>
  useAuthorizationRequired(portalGlobalsReadStatement);

export const useCanUpdatePortalGlobals = (): boolean =>
  useAuthorizationRequired(portalGlobalsUpdateStatement);

export const withPlantProfilesPageReadAccess = <P extends object>(
  Component: ComponentType<P>,
  otherwise?: ReactNode
): FC<P> =>
  withAuthenticationRequired(
    withFeatureFlag(
      { flag: FeatureFlag.CATEGORY_COLLECTION, noFlag: Path.FLEET },
      withAuthorizationRequired(
        plantProfilesPageReadStatement,
        Component,
        otherwise
      )
    )
  );

export const withPlantProfileListReadAccess = <P extends object>(
  Component: ComponentType<P>
): FC<P> =>
  withAuthorizationRequired(plantProfilesListReadStatement, Component);

export const withPlantProfileCustomerUpdateAccess = <P extends object>(
  Component: ComponentType<P>
): FC<P> =>
  withAuthorizationRequired(plantProfilesCustomerUpdateStatement, Component);

export const withPortalGlobalsReadAccess = <P extends object>(
  Component: ComponentType<P>
): FC<P> => withAuthorizationRequired(portalGlobalsReadStatement, Component);
